import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  IconButton,
  InputAdornment,
  Switch,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  VpnKey,
  PersonAdd,
  AccountCircle,
  LockOutlined,
} from "@mui/icons-material";
import { useUsuario } from "../../../Hooks/UsuarioHook";
import { SelecionarContaLogin } from "../../../Components/SelecionarContaLogin";
import { useNavigate, useSearchParams } from "react-router-dom";
import { app_base_url } from "../../../Utils/Urls";
import { limpa_mascara_cpf_cnpj } from "../../../Utils/Mascara/MascaraCpf";
import { validaCpf } from "../../../Utils/Validadores/ValidCpf";
import { validaCnpj } from "../../../Utils/Validadores/ValidCnpj";
import { Logo } from "../../../Components/Logo";
import { RegistroProtocoloEletronico } from "../../../Utils/RegistroDemaisSistema";
import { useEntidade } from "../../../Hooks/EntidadeHook";
import { ThemeToggle } from "../../../Components/ThemeToggle";

export const Login = () => {
  const { logar, contasMesmoUsuario } = useUsuario();
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [openSelecionarConta, setOpenSelecionarConta] = useState(false);
  const [manterAutenticado, setManterAutenticado] = useState(false);
  const [searchParams] = useSearchParams();
  const { entidades, setEntidadeFocus } = useEntidade();
  const banco = searchParams.get("entidade");
  // Pega o parâmetro "entidade" da URL
  const entidadeBanco = searchParams.get("entidade");

  useEffect(() => {
    if (entidadeBanco) {
      const entidadeSelecionada = entidades.find(
        (entidade) => entidade.banco === entidadeBanco
      );

      if (entidadeSelecionada) {
        setEntidadeFocus(entidadeSelecionada);
      }
    }
  }, [entidadeBanco, entidades]);



  const navigate = useNavigate();

  useEffect(() => {
    if (contasMesmoUsuario && contasMesmoUsuario.length > 1) {
      setOpenSelecionarConta(true);
    }
  }, [contasMesmoUsuario]);

  const handleLogin = () => {
    const limpaUsuario = limpa_mascara_cpf_cnpj(usuario);

    if (limpaUsuario.length === 11 && validaCpf(limpaUsuario)) {
      logar(limpaUsuario, senha, "cpf", manterAutenticado);
    } else if (limpaUsuario.length === 14 && validaCnpj(limpaUsuario)) {
      logar(limpaUsuario, senha, "cnpj", manterAutenticado);
    } else {
      logar(usuario, senha);
    }
  };

  const botaoBloqueado =
    usuario.length < 11 || senha.length < 1;

  // estilo compartilhado dos campos: cantos suaves, hover e brilho no foco
  const campoSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      bgcolor: (theme: any) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.03)"
          : "rgba(15,23,42,0.02)",
      transition: "box-shadow .2s, background-color .2s",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "primary.main",
      },
      "&.Mui-focused": {
        boxShadow: "0 0 0 4px rgba(79,142,247,0.15)",
      },
    },
  } as const;


  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{ minHeight: "100vh", position: "relative", p: 2 }}
    >
      {/* Alternar tema claro/escuro */}
      <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
        <ThemeToggle />
      </Box>

      <Grid item xs={12} sm={8} md={5} lg={4}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 460,
            mx: "auto",
            p: { xs: 3.5, sm: 5 },
            borderRadius: 4,
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 20px 60px rgba(0,0,0,0.5)"
                : "0 20px 60px rgba(15,23,42,0.10)",
          }}
        >
          {/* Logo com destaque (chip) para contraste em ambos os temas */}
          <Box display="flex" justifyContent="center" mb={3.5}>
            <Box
              sx={{
                px: 3.5,
                py: 2,
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid rgba(15,23,42,0.06)",
                boxShadow: "0 4px 14px rgba(15,23,42,0.08)",
                display: "flex",
              }}
            >
              <Logo w={130} />
            </Box>
          </Box>

          {/* Título */}
          <Typography
            component="h1"
            textAlign="center"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.25rem", sm: "1.4rem" },
              letterSpacing: "-0.3px",
              color: "text.primary",
              mb: 4,
            }}
          >
            Login Central
          </Typography>

          {/* Campos de Login */}
          <TextField
            fullWidth
            label="Usuário ou CPF/CNPJ"
            variant="outlined"
            margin="normal"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            sx={campoSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Senha"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            margin="normal"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            sx={campoSx}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !botaoBloqueado) handleLogin();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Opção de manter autenticado */}
          <Box
            onClick={() => setManterAutenticado((v) => !v)}
            sx={{
              mt: 1.5,
              px: 1.75,
              py: 0.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 2.5,
              border: 1,
              borderColor: manterAutenticado ? "primary.main" : "divider",
              bgcolor: manterAutenticado
                ? "rgba(79,142,247,0.08)"
                : "transparent",
              cursor: "pointer",
              transition: "all .2s",
              "&:hover": { borderColor: "primary.main" },
            }}
          >
            <Box>
              <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                Manter autenticado
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Continue conectado neste dispositivo
              </Typography>
            </Box>
            <Switch
              checked={manterAutenticado}
              onChange={(e) => setManterAutenticado(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              color="primary"
            />
          </Box>

          {/* Botão de Login */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            sx={{
              mt: 2,
              py: 1.3,
              fontWeight: 700,
              fontSize: "1rem",
              borderRadius: 2,
              background: "linear-gradient(135deg, #2563eb, #4f8ef7)",
              boxShadow: "0 8px 20px rgba(37,99,235,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #1e40af, #3b82f6)",
                boxShadow: "0 10px 26px rgba(37,99,235,0.45)",
              },
              "&.Mui-disabled": {
                background: (theme) => theme.palette.action.disabledBackground,
                boxShadow: "none",
              },
            }}
            disabled={botaoBloqueado}
            onClick={handleLogin}
          >
            Entrar
          </Button>

          {/* Separador */}
          <Divider sx={{ my: 3 }} />

          {/* Links de Ações */}
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Button
              sx={{
                textTransform: "none",
                color: "primary.main",
                fontWeight: "bold",
              }}
              startIcon={<VpnKey />}
              onClick={() => navigate(`${app_base_url}/recuperar-senha/${banco ? `?entidade=${banco}` : ""}`)}
            >
              Esqueci minha senha
            </Button>

            <Button
              sx={{
                textTransform: "none",
                color: "primary.main",
                fontWeight: "bold",
              }}
              startIcon={<PersonAdd />}
              onClick={() => navigate(`${app_base_url}/autocadastro/${banco ? `?entidade=${banco}` : ""}`)}
            >
              Criar Conta
            </Button>
          </Box>

          {/* Rodapé: crédito */}
          <Typography
            variant="caption"
            textAlign="center"
            display="block"
            sx={{ mt: 3, color: "text.secondary" }}
          >
            Desenvolvido por{" "}
            <Box
              component="a"
              href="https://sinsoft.com.br/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Sinsoft Informática
            </Box>
          </Typography>
        </Box>
      </Grid>

      {/* Modal para Seleção de Conta */}
      <SelecionarContaLogin
        open={openSelecionarConta}
        onClose={() => setOpenSelecionarConta(false)}
        senhaHelper={senha}
        manterAutenticado={manterAutenticado}
      />
    </Grid>
  );
};
